// src/components/DigitalPlatforms.js
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { getVaultLabels } from "./PasswordVault";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import PageScaffold from "./ui/PageScaffold";
import VideoLightbox from "./ui/VideoLightbox";
import { useAccess } from "../access/AccessContext";
import "../styles/layout.css";

export default function DigitalPlatforms({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  const [platforms, setPlatforms] = useState([]);
  const [vaultLabels, setVaultLabels] = useState([]);
  const [form, setForm] = useState({
    name: "",
    url: "",
    username: "",
    notes: "",
    passwordLabel: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  // Header helpers
  const [showVideo, setShowVideo] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  // Read-only view modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const firstFieldRef = useRef();

  // Subscribe to platforms
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, "digitalPlatforms"),
      where("uid", "==", user.uid)
    );

    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const aMs =
            a?.created?.toMillis?.() ??
            (a?.created?.seconds ? a.created.seconds * 1000 : 0);
          const bMs =
            b?.created?.toMillis?.() ??
            (b?.created?.seconds ? b.created.seconds * 1000 : 0);
          return bMs - aMs;
        });
        setPlatforms(rows);
      },
      (err) => {
        console.error("[DigitalPlatforms] listen failed", err);
        setError(err?.message || "Failed to load digital platforms.");
      }
    );
  }, [user?.uid]);

  // Load vault labels
  useEffect(() => {
    if (!user) return;
    getVaultLabels(user).then(setVaultLabels);
  }, [user]);

  function openForm(platform = null) {
    if (readOnly) return; // guard
    if (platform) {
      setEditingId(platform.id);
      setForm({
        name: platform.name || "",
        url: platform.url || "",
        username: platform.username || "",
        notes: platform.notes || "",
        passwordLabel: platform.passwordLabel || "",
      });
    } else {
      setEditingId(null);
      setForm({ name: "", url: "", username: "", notes: "", passwordLabel: "" });
    }
    setError("");
    setShowForm(true);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setError("");
  }

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.username) {
      setError("Name and username are required.");
      return;
    }
    try {
      if (editingId) {
        await updateDoc(doc(db, "digitalPlatforms", editingId), {
          ...form,
          updated: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "digitalPlatforms"), {
          ...form,
          uid: user.uid,
          created: serverTimestamp(),
          updated: serverTimestamp(),
        });
      }
      closeForm();
    } catch (e) {
      setError("Save failed: " + (e?.message || e));
    }
  }

  async function handleDelete(p) {
    if (!window.confirm("Delete this platform?")) return;
    await deleteDoc(doc(db, "digitalPlatforms", p.id || editingId));
    if (editingId === (p?.id || editingId)) closeForm();
  }

  // ------- UI helpers -------
  function faviconFor(url) {
    try {
      if (!url) return null;
      const u = new URL(url.startsWith("http") ? url : "https://" + url);
      return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=64`;
    } catch {
      return null;
    }
  }

  const actionsDesktop = (
    <>
      <button
        className="btn dp-help read"
        onClick={() => setShowDrawer(true)}
        data-allow-readonly
      >
        <FiEye className="dp-help-ico" />
        Read how it works
      </button>
      <button
        className="btn dp-help see"
        onClick={() => setShowVideo(true)}
        data-allow-readonly
      >
        <FaYoutube className="dp-help-ico" />
        See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="dp-help-row">
      <button
        className="btn dp-help read"
        onClick={() => setShowDrawer(true)}
        data-allow-readonly
      >
        <FiEye className="dp-help-ico" />
        Read how it works
      </button>
      <button
        className="btn dp-help see"
        onClick={() => setShowVideo(true)}
        data-allow-readonly
      >
        <FaYoutube className="dp-help-ico" />
        See how it works
      </button>
    </div>
  );

  return (
    <>
      <PageScaffold
        title="Digital Platforms"
        onAdd={!readOnly ? () => openForm() : undefined}
        heroSrc="/lastwish-account-Platforms.jpg"
        heroAlt="Digital Platforms"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
        
      >
        {/* Full-width list, 1 column, actions on the right */}
        <div className="dp-list">
          {platforms.length === 0 ? (
            <div className="empty-state">No platforms yet.</div>
          ) : (
            platforms.map((p) => {
              const fav = faviconFor(p.url);
              const onPrimary = readOnly
                ? () => {
                    setViewItem(p);
                    setViewOpen(true);
                  }
                : () => openForm(p);
              const label = readOnly ? "View" : "Edit";
              return (
                <div className="card dp-item" key={p.id}>
                  <div className="dp-left">
                    {fav ? (
                      <img className="tile-thumb" src={fav} alt="" />
                    ) : (
                      <div
                        className="tile-thumb"
                        style={{
                          display: "grid",
                          placeItems: "center",
                          background: "#f3f3f3",
                        }}
                      >
                        🔗
                      </div>
                    )}
                  </div>

                  <div className="dp-main">
                    <div className="tile-title">{p.name}</div>
                    <div className="tile-sub dp-url">{p.url}</div>
                    <div className="tile-sub dp-user">User: {p.username}</div>
                  </div>

                  <div className="dp-actions">
                    <button
                      className="btn btn-main"
                      onClick={onPrimary}
                      {...(readOnly ? { "data-allow-readonly": "1" } : {})}
                    >
                      {label}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PageScaffold>

      {/* ===== Form Modal (owner) — actions reverted (no accordion) ===== */}
      {showForm && (
        <div className="modal-bg" onClick={closeForm}>
          <form
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
          >
            <h3>{editingId ? "Edit Platform" : "Add Platform"}</h3>

            <div className="modal-body">
              <input
                name="name"
                ref={firstFieldRef}
                className="input"
                placeholder="Platform"
                value={form.name}
                onChange={handleChange}
                required
              />
              <input
                name="url"
                className="input"
                placeholder="URL (optional)"
                value={form.url}
                onChange={handleChange}
              />
              <input
                name="username"
                className="input"
                placeholder="Username"
                value={form.username}
                onChange={handleChange}
                required
              />
              <select
                name="passwordLabel"
                className="input"
                value={form.passwordLabel}
                onChange={handleChange}
              >
                <option value="">Link to Password…</option>
                {vaultLabels.map((label) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
              </select>
              <textarea
                name="notes"
                className="input"
                placeholder="Notes (optional)"
                rows={2}
                value={form.notes}
                onChange={handleChange}
              />

              {error && (
                <div className="empty-state" style={{ color: "#9b1c1c" }}>
                  {error}
                </div>
              )}

              {/* Reverted plain action row */}
              <div className="form-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn btn-main" type="submit">
                  {editingId ? "Update" : "Save"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDelete({ id: editingId })}
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={closeForm}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ===== Read-only View modal ===== */}
      {viewOpen && viewItem && (
        <div className="modal-bg" onClick={() => setViewOpen(false)}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 620 }}
          >
            <h3>Platform</h3>
            <div className="modal-body">
              <div
                className="card"
                style={{ padding: 12, display: "grid", gap: 8 }}
              >
                <div>
                  <b>Name:</b> {viewItem.name || "-"}
                </div>
                <div>
                  <b>URL:</b> {viewItem.url || "-"}
                </div>
                <div>
                  <b>Username:</b> {viewItem.username || "-"}
                </div>
                {viewItem.passwordLabel && (
                  <div>
                    <b>Password Label:</b> {viewItem.passwordLabel}
                  </div>
                )}
                {viewItem.notes && (
                  <div>
                    <b>Notes:</b> {viewItem.notes}
                  </div>
                )}
              </div>
              <div className="form-actions" style={{ marginTop: 10 }}>
                {viewItem.url && (
                  <a
                    className="btn btn-main"
                    href={
                      viewItem.url.startsWith("http")
                        ? viewItem.url
                        : `https://${viewItem.url}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    data-allow-readonly
                  >
                    Open
                  </a>
                )}
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={() => window.print()}
                  data-allow-readonly
                >
                  Print
                </button>
                <a
                  className="btn btn-main"
                  href={`mailto:?subject=Platform info&body=${encodeURIComponent(
                    `Name: ${viewItem.name || "-"}\nURL: ${
                      viewItem.url || "-"
                    }\nUser: ${viewItem.username || "-"}\nNotes: ${
                      viewItem.notes || "-"
                    }`
                  )}`}
                  data-allow-readonly
                >
                  Forward
                </a>
                <button
                  className="btn btn-cancel"
                  type="button"
                  onClick={() => setViewOpen(false)}
                  data-allow-readonly
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Video modal (VideoLightbox) — FIXED props ===== */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/z5V0Z-hrtSI?si=xiwvucsna5eECf_X"
        title="How it works (video)"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* ===== Right drawer ===== */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How Digital Platforms work</div>
              <div className="dp-drawer-tools">
                <button
                  className="btn btn-main"
                  onClick={() => window.print()}
                  type="button"
                  data-allow-readonly
                >
                  Print
                </button>
                <button
                  className="btn btn-cancel"
                  onClick={() => setShowDrawer(false)}
                  type="button"
                  data-allow-readonly
                >
                  Close
                </button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>
                Use this section to keep track of accounts like banking,
                utilities, subscriptions, and social platforms. You can link an
                existing password entry from your Vault by selecting a label.
              </p>
              <ol>
                <li>Click the + button to add a platform.</li>
                <li>Optionally paste the URL so we can show the site’s logo.</li>
                <li>
                  Link a “Password Label” from your Vault for easy
                  cross-reference.
                </li>
                <li>Use Edit to update or Delete to remove.</li>
              </ol>
              <p>
                Only you (and your designated managers when appropriate) can see
                this list.
              </p>
            </div>
          </aside>
        </div>
      )}

      {/* Page-scoped styles (unchanged) */}
      <style>{`
        :root { --topbar-h: 56px; --orange:#f15822; --blue:#8cade1; }

        .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .dp-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .dp-help.read { background: #650202; color: #fff; }
        .dp-help.see  { background: #003353; color: #fff; }
        .dp-help-ico { font-size: 1.05rem; }

        .dp-intro { margin: 4px 0 10px; color: #2a0516; }

        .dp-list { display: grid; grid-template-columns: 1fr; gap: 16px; }
        .dp-item { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 12px; padding: 12px; }
        .dp-left { width: 42px; }
        .dp-main { min-width: 0; }
        .dp-main .tile-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .dp-url, .dp-user { color: #657899; }

        .dp-actions { display: flex; gap: 8px; align-items: center; justify-self: end; }

        @media (max-width: 700px) {
          .dp-item { grid-template-columns: auto 1fr auto; }
          .dp-main .tile-title { font-size: 0.98rem; }
          .dp-url, .dp-user { font-size: 0.86rem; }
        }

        .dp-video { position: relative; width: 100%; padding-top: 56.25%; border-radius: 10px; overflow: hidden; background: #000; margin-bottom: 12px; }
        .dp-video iframe { position: absolute; inset: 0; width: 100%; height: 100%; }

        .dp-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
        .dp-drawer {
          position: fixed; top: var(--topbar-h, 56px); right: 0;
          height: calc(100vh - var(--topbar-h, 56px)); width: min(520px, 95vw);
          background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15);
          border-top-left-radius: 12px; border-bottom-left-radius: 12px;
          display: flex; flex-direction: column; overflow: hidden; animation: dp-slide-in .22s ease-out both;
        }
        @keyframes dp-slide-in { from { transform: translateX(20px); opacity:0; } to { transform: translateX(0); opacity:1; } }
        .dp-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title { font-weight:700; color:#2a0516; }
        .dp-drawer-tools { display:flex; gap:8px; }
        .dp-drawer-body { padding:16px; overflow:auto; }
      `}</style>
    </>
  );
}
